// The load affordances (P2.5) — UX §5.3, FR-01.
//
// "Drag a file anywhere over a deck half → the whole deck becomes a drop target with a colored
// outline (deck identity color). On drop: waveform appears ≤2 s, BPM/key placeholders fill in as
// analysis completes; a thin progress line runs along the overview strip."
//
// Two of those three are P2.5's. The waveform itself is P2.6, and BPM/key need analysis stages that
// are M3 — a placeholder that says "—" is honest; one that says "128.0" would be inventing data.
//
// The outline lives on the deck panel rather than on this component, because UX §5.3 says the
// *whole deck half* is the target and a panel-sized region inside a panel is not that. This
// component owns the picker, the status, and the progress line; the panel owns the outline and is
// handed the handlers.

import type { JSX } from 'react';
import { useRef } from 'react';

import type { TrackAnalysis } from '../../audio/analysis';
import { bpmOf, type GridOverride } from '../../platform/types/grid-edit';
import { describeKey, semitonesForRate } from './camelot';
import { GridTools } from './GridTools';
import { PerformancePads } from './PerformancePads';
import { PitchFader } from './PitchFader';
import { SyncPanel } from './SyncPanel';
import { Transport } from './Transport';
import type { SyncPartner, TrackLoaderApi } from './useTrackLoader';

/**
 * A track's total length. **Rounds, and the difference is not cosmetic.**
 *
 * `Math.floor` was here first and it showed a 10-second file as `0:09`. Not a rounding nicety — a
 * systematic off-by-one-second on almost every real track, and it took ten seconds of driving the
 * UI to see (no unit test would have: the arithmetic is correct, the *input* is what surprises).
 *
 * The cause is P0.5's finding arriving where nobody expected it. `decodeAudioData` resamples to the
 * context's rate, this machine's context runs at **44.1 kHz**, and a 48 kHz file therefore lands on
 * 440,999 frames rather than 441,000 — a duration of 9.999977 s. Floor makes that 9. Since a
 * source rate matching the device rate is the exception rather than the rule, so is a track whose
 * length displays correctly.
 *
 * Rounding is also simply the right operation for this number: a *total* is a fixed property of the
 * file and 9.999977 s is ten seconds to any human. **Elapsed** time is the opposite case and floors
 * — 9.9 s into a track really has not reached 0:10 — which is P2.7's readout, not this one. Worth
 * keeping the two apart when that lands.
 */
export function formatDuration(seconds: number): string {
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  // The carry works out: round(59.7) is 60, which is 1 minute and 0 seconds, not "0:60".
  return `${String(minutes)}:${String(whole % 60).padStart(2, '0')}`;
}

/**
 * FR-10's measurement, in words (P3.2).
 *
 * **Every non-`done` outcome gets its own sentence.** The whole reason `StageOutcome` has four
 * states is that "not measured yet", "not implemented", "measured as silent" and "it threw" are
 * different facts, and a readout that renders all four as "—" throws that distinction away at the
 * one place a human could have used it.
 *
 * ⚠️ **`applied` is not decoration — without it this readout was false half the time.** Reported by
 * the stakeholder on 4 Sep: untick *Match levels* and the label beside it correctly says "off"
 * while this line went on reading "+11.4 dB". Two readouts, one state, opposite answers — and the
 * one with the number is the one a person believes. It is the same shape as the two disagreeing
 * readouts P3.12 found, and the reason the bypass exists at all is that FR-10 is a claim about what
 * two tracks *sound* like, which cannot be judged while the screen is describing a gain the deck is
 * not applying.
 *
 * The LUFS stays either way: that is a measurement of the track and is true whatever the deck does
 * with it. Only the gain is conditional, because only the gain is an action.
 */
export function describeLoudness(
  loudness: TrackAnalysis['stages']['loudness'] | undefined,
  applied: boolean,
): string {
  if (loudness === undefined || loudness.status === 'pending') {
    return 'measuring…';
  }
  if (loudness.status === 'skipped' || loudness.status === 'failed') {
    return loudness.reason;
  }

  const { lufs, gain, reason } = loudness.value;
  if (lufs === null) {
    return reason === 'too-short' ? 'too short to measure' : 'silent';
  }

  // Signed, and to one decimal, because the number a DJ compares is the *difference* between two
  // tracks and a rounded integer hides a decibel of it.
  const db = 20 * Math.log10(gain);
  const sign = db >= 0 ? '+' : '−';
  const limited = reason === 'normalised' ? '' : ` (${reason})`;
  const measured = `${sign}${Math.abs(db).toFixed(1)} dB`;
  // Named rather than blanked. "What would it do if I ticked the box" is the question the A/B is
  // for, so throwing the number away would cost the comparison the bypass exists to enable; saying
  // it is not applied costs nothing and is the part that was missing.
  return applied
    ? `${lufs.toFixed(1)} LUFS · ${measured}${limited}`
    : `${lufs.toFixed(1)} LUFS · matching off (${measured} available)`;
}

/**
 * How sure the tempo has to be before the readout stops drawing attention to itself.
 *
 * P3.3's rule: "a UI that cannot tell 'confident' from 'guessing' cannot prompt for correction".
 * FR-13's manual tools (P3.12) exist because roughly one track in twenty comes out wrong, and the
 * only thing that turns that from a lie into a prompt is showing which twenty. Above this the number
 * stands alone; below it, it says so and shows its working.
 *
 * A single threshold, and it changes nothing about the answer — only whether the reader is invited
 * to check it.
 */
const TEMPO_SURE_ENOUGH = 0.6;

/**
 * FR-09's BPM readout, with P3.3's confidence attached (P3.3).
 *
 * Every non-`done` outcome gets its own words, for the same reason `describeLoudness` does: "not
 * measured yet", "not implemented", "no pulse in this track" and "it threw" are four different
 * facts, and rendering all four as "—" throws the distinction away at the one place a human could
 * have used it.
 */
export function describeTempo(
  tempo: TrackAnalysis['stages']['tempo'] | undefined,
  override?: GridOverride | null,
  rate = 1,
): string {
  // ⚠️ **The correction is consulted first, and it has to be** (P3.12). Without this the readout
  // reports the *detected* tempo while the beatgrid panel three lines below reports the corrected
  // one — 120.0 against 99.41 for the same track, caught in the browser exactly like that. Two
  // readouts disagreeing about one number is worse than either of them being wrong alone, because
  // the user cannot tell which to believe or whether their correction took.
  //
  // No hedge on a corrected grid either, for the reason `effectiveConfidence` gives: the detector's
  // confidence describes the detector's answer, and this is not that answer.
  let trackBpm: number;
  let hedge = '';

  if (override != null) {
    trackBpm = bpmOf(override.beatSeconds);
  } else {
    if (tempo === undefined || tempo.status === 'pending') {
      return '…';
    }
    if (tempo.status === 'skipped' || tempo.status === 'failed') {
      return '—';
    }
    const { bpm, confidence, reason } = tempo.value;
    if (bpm === null) {
      return reason === 'too-short' ? 'too short' : 'no beat';
    }
    trackBpm = bpm;
    hedge =
      confidence >= TEMPO_SURE_ENOUGH ? '' : `?  ${(confidence * 100).toFixed(0)}% sure`;
  }

  // ⚠️ **FR-09 asks for "BPM (live, reflecting pitch)", and until this it did not.** A deck at +6%
  // showed the track's 120.0 while playing at 127.2 — the same class of bug as the key readout
  // ignoring the fader, which P3.13 fixed one half of and missed the other. The number a DJ needs is
  // the one they are matching against, and that is the tempo coming out of the speakers.
  const live = trackBpm * (rate > 0 ? rate : 1);
  const liveText = live.toFixed(1);
  const trackText = trackBpm.toFixed(1);

  // The track's own tempo is appended **only when the two would print differently**, which is
  // self-regulating: at rest there is no suffix, and a nudge too small to move the first decimal
  // does not add one either. It is there because the beatgrid panel below shows the *track's* BPM,
  // and two visibly different numbers with no explanation is exactly the confusion P3.12 fixed.
  return liveText === trackText
    ? `${liveText}${hedge}`
    : `${liveText}${hedge} · track ${trackText}`;
}

/**
 * Is this tempo one the UI should invite the user to correct? (P3.13)
 *
 * P3.13's rule: "a low-confidence BPM is displayed differently from a confident one (P3.12's
 * invitation to correct)". **Differently, not merely annotated** — `describeTempo` has appended a
 * `?` and a percentage since P3.3, which is honest text that reads as part of the number and is
 * easy to slide past. This drives a *class*, so the readout is visibly flagged and the connection
 * to the grid tools below it is something a user notices rather than parses.
 *
 * A correction removes the flag outright: the detector's confidence describes the detector's
 * answer, and once a human has supplied the grid there is nothing left to be unsure about.
 */
export function isTempoUncertain(
  tempo: TrackAnalysis['stages']['tempo'] | undefined,
  override?: GridOverride | null,
): boolean {
  if (override != null) {
    return false;
  }
  if (tempo?.status !== 'done') {
    return false;
  }
  return tempo.value.bpm !== null && tempo.value.confidence < TEMPO_SURE_ENOUGH;
}

export interface TrackLoaderProps {
  readonly loader: TrackLoaderApi;
  /**
   * The other deck (P3.9). Sync is the one control that is *about* the pair rather than about this
   * deck, so it is the one prop that reaches across — passed rather than reached for, so this
   * component still knows nothing about how many decks exist.
   */
  readonly partner?: SyncPartner | undefined;
}

export function TrackLoader({ loader, partner }: TrackLoaderProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const busy = loader.phase === 'decoding' || loader.phase === 'analysing';
  const loudness = loader.analysis?.stages.loudness;
  /** Whether FR-10's gain is reaching the audio, which is what the readout must describe. */
  const loudnessApplied = loader.appliedGain !== 1;
  const tempo = loader.analysis?.stages.tempo;
  const key = loader.analysis?.stages.key;

  return (
    <div className="stack">
      {/* FR-01 wants drag-drop *and* a picker. The input is visually hidden rather than absent:
          a `display: none` input cannot be focused, and keyboard users would lose the only route
          to loading a track — the drop target is pointer-only by nature. */}
      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        // No `accept` — see `PICKER_ACCEPTS_EVERYTHING` in formats.ts. A filter here hid the
        // stakeholder's whole music folder while drag-and-drop took the same files happily.
        onChange={(event) => {
          const file = event.currentTarget.files?.item(0) ?? null;
          if (file !== null) {
            loader.load(file);
          }
          // Cleared so choosing the *same* file twice fires `change` again. Without this, a user
          // who reloads the track they just loaded gets silence and no explanation.
          event.currentTarget.value = '';
        }}
      />

      <button
        type="button"
        onClick={() => {
          inputRef.current?.click();
        }}
        disabled={busy}
        // Named per deck since P3.7: two identical "Load a track…" buttons on one screen are two
        // controls a screen reader — and a Playwright locator — cannot tell apart.
        aria-label={`Load a track into deck ${loader.deckId.toUpperCase()}`}
      >
        {busy ? 'Loading…' : 'Load a track…'}
      </button>

      <p className="micro">Or drag a file anywhere onto this deck.</p>

      {/* The overview strip's progress line (UX §5.3). Present from `decoding` so the strip does
          not pop into existence halfway through a load — it fills during analysis, driven by the
          rAF loop in useTrackLoader writing `style.width` directly. */}
      {busy && (
        <div className="stack stack--tight">
          <span className="bar bar--progress">
            <div
              ref={loader.progressRef}
              className={`bar__fill bar__fill--deck-${loader.deckId}`}
            />
          </span>
          <span className="micro">
            {loader.phase === 'decoding' ? 'Decoding…' : 'Building waveform…'}
          </span>
        </div>
      )}

      {loader.phase === 'ready' && <Transport loader={loader} />}

      {/* FR-05, and the first control in the app that streams into the audio thread (P3.6). Below
          the transport because that is where it sits on every deck ever built. */}
      {loader.phase === 'ready' && <PitchFader loader={loader} />}

      {/* FR-07's sync (P3.9) — one action, and the live phase error beside it. `SyncPanel` has the
          reasoning for both. */}
      {loader.phase === 'ready' && <SyncPanel loader={loader} partner={partner} />}

      {/* FR-13's manual grid tools (P3.12), directly under sync.
          ⚠️ **P3.12 asks for these "where a wrong grid is noticed, which is the waveform"** — and
          today there is one shared waveform lane at the top of the layout, deck A's, with no
          per-deck lane to attach to. This is the closest the current layout gets: a bad grid
          announces itself through Sync flam and through loops refusing to snap, and both of those
          readouts are its immediate neighbours here. **P3.13 gives each deck its own lane and is the
          moment to move this beside it** — parked in `BACKLOG-NOTES.md` so it is not forgotten. */}
      {loader.phase === 'ready' && <GridTools loader={loader} />}

      {/* FR-03's hot cues and FR-04's loops (P3.10). Below sync because that is the order a hand
          reaches for them: get the tracks together, then work the one that is cued. */}
      {loader.phase === 'ready' && <PerformancePads loader={loader} />}

      {loader.phase === 'ready' && loader.trackTitle !== null && (
        <dl className="track-summary">
          <dt className="micro">Track</dt>
          {/* `title` too, because a long filename is elided and the whole point of showing it is
              that the user recognises their file. */}
          <dd className="track-summary__title" title={loader.trackTitle}>
            {loader.trackTitle}
          </dd>

          <dt className="micro">Length</dt>
          <dd className="mono">
            {loader.durationSeconds === null
              ? '—'
              : formatDuration(loader.durationSeconds)}
          </dd>

          <dt className="micro">BPM · Key</dt>
          {/* UX §5.3's "BPM/key placeholders fill in as analysis completes", finally both halves:
              BPM since P3.3, key since P3.5. Camelot first because that is what a DJ reads off it. */}
          <dd
            className={
              'mono' + (isTempoUncertain(tempo, loader.gridOverride) ? ' unsure' : '')
            }
          >
            {describeTempo(tempo, loader.gridOverride, loader.pitchRate)} ·{' '}
            {/* The key the speakers are producing, not the one in the file: vinyl mode transposes
                with the rate, and since P3.11 ruled keylock out of v1 that is permanent. */}
            {key === undefined || key.status === 'pending'
              ? '…'
              : key.status === 'done'
                ? describeKey(key.value, semitonesForRate(loader.pitchRate))
                : '—'}
          </dd>

          <dt className="micro">Loudness</dt>
          {/* Greyed as well as worded when the gain is bypassed — UX §5.1's cross-theme rule asks
              that meaning survive without colour, so the sentence carries it and the shade only
              reinforces. `appliedGain` rather than `normalise`, because it is the number the deck
              is actually using and the two could in principle differ. */}
          <dd className={'mono' + (loudnessApplied ? '' : ' inactive')}>
            {describeLoudness(loudness, loudnessApplied)}
          </dd>

          <dt className="micro">Waveform ready in</dt>
          {/* FR-12's claim, on screen (P3.8). "Effectively instant and never re-analyzes" is two
              claims, and a duration only evidences the first — a fast machine and a cache hit look
              identical in milliseconds. Saying which happened is what makes the second one
              checkable by looking, and it is why `analysisSource` exists at all. */}
          <dd className="mono">
            {loader.elapsedMillis === null
              ? '—'
              : `${loader.elapsedMillis.toFixed(0)} ms` +
                (loader.analysisSource === 'cache'
                  ? ' · from cache, not re-analysed'
                  : loader.analysisMillis === null
                    ? ''
                    : ` (${loader.analysisMillis.toFixed(0)} ms analysis)`)}
          </dd>
        </dl>
      )}

      {/* FR-10's bypass. It exists because the requirement is a claim about what two tracks sound
          like side by side, and the only instrument that can check that is an ear with an A/B —
          see `TrackLoaderApi.normalise`. A plain checkbox rather than a styled toggle: it is
          keyboard-operable and labelled for free, and UX §5.1's tokens have no switch yet. */}
      {loader.phase === 'ready' && (
        <label className="micro">
          <input
            type="checkbox"
            checked={loader.normalise}
            onChange={(event) => {
              loader.setNormalise(event.currentTarget.checked);
            }}
          />{' '}
          Match levels (FR-10) — {loader.appliedGain === 1 ? 'off' : 'applied'}
        </label>
      )}

      {loader.phase === 'failed' && loader.error !== null && (
        // `role="alert"` so a screen reader announces it — a failure the user cannot see reported
        // is a failure they will read as the app being broken (UX §7's operability line).
        <p className="notice notice--error" role="alert">
          {loader.error}
        </p>
      )}
    </div>
  );
}
